import React, {useEffect, useRef, useState} from 'react';
import {Button} from '@/components/ui/button';
import {useNavigate} from 'react-router-dom';
import {AlertCircle, ArrowRight, Clock, LineChart, Percent, Users} from 'lucide-react';
import {motion} from 'framer-motion';
import {useTranslation} from 'react-i18next';
import SavingsMetricCard from '@/components/savings/SavingsMetricCard';

const SwipeSlideSection = () => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState(50); // Default to center
    const [isDragging, setIsDragging] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [hovered, setHovered] = useState(false);
    const [aiMetricOpacity, setAiMetricOpacity] = useState(1);
    const [tradMetricOpacity, setTradMetricOpacity] = useState(1);
    const titleRef = useRef<HTMLHeadingElement>(null);

    const {t} = useTranslation('common');

    // Effect to update metrics opacity based on slider position
    useEffect(() => {
        // AI side metrics fade out as slider moves right (increasing position means revealing less of AI side)
        setAiMetricOpacity(Math.max(0, Math.min(1, (100 - position) / 50)));

        // Traditional side metrics fade out as slider moves left (increasing position means revealing more of trad side)
        setTradMetricOpacity(Math.max(0, Math.min(1, position / 50)));
    }, [position]);

    // Handle mouse events for the slider
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        updateSliderPosition(e);
        e.preventDefault();
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging || isHovering) {
            updateSliderPosition(e);
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const handleMouseEnter = () => {
        setIsHovering(true);
    };

    const handleMouseLeave = () => {
        setIsHovering(false);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        setIsDragging(true);
        updateSliderPositionFromTouch(e);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!isDragging) return;
        updateSliderPositionFromTouch(e);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
    };

    // Helper function to update slider position
    const updateSliderPosition = (e: React.MouseEvent) => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const width = rect.width;
        const newPosition = Math.max(0, Math.min(100, (x / width) * 100));
        setPosition(newPosition);
    };

    // Helper function for touch events
    const updateSliderPositionFromTouch = (e: React.TouchEvent) => {
        if (!containerRef.current || !e.touches[0]) return;

        const rect = containerRef.current.getBoundingClientRect();
        const x = e.touches[0].clientX - rect.left;
        const width = rect.width;
        const newPosition = Math.max(0, Math.min(100, (x / width) * 100));
        setPosition(newPosition);
    };

    // Add global mouse/touch event listeners
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
        };

        window.addEventListener('mouseup', handleGlobalMouseUp);
        window.addEventListener('touchend', handleGlobalMouseUp);

        return () => {
            window.removeEventListener('mouseup', handleGlobalMouseUp);
            window.removeEventListener('touchend', handleGlobalMouseUp);
        };
    }, []);

    return (
        <section className="w-full py-16 md:py-24 bg-white">
            <div className="container mx-auto px-4 md:px-6">


                {/* Before/After Slider */}
                <div
                    className="relative overflow-hidden rounded-xl shadow-xl border border-gray-200 max-w-5xl mx-auto"
                    style={{height: '600px'}}
                    ref={containerRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >

                    {/* "After" Content (AI-Optimized) */}
                    <div
                        className="absolute inset-0 bg-gradient-to-br from-emerald-50 to-emerald-100 p-8 md:p-12 overflow-y-auto z-10"
                        style={{clipPath: `inset(0 ${100 - position}% 0 0)`}}>
                        <div className="h-full flex flex-col justify-center max-w-xl mx-auto">


                            <h1
                                ref={titleRef}
                                className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight text-center"
                            >

Customised AI Platform
                                <br/>
                                <span
                                    className="bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent">


                </span>
                            </h1>


                            <h3 className="text-3xl md:text-4xl font-bold mb-6">
                                <span
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                                    AI-Optimized Compliance
                                </span>
                            </h3>
                            <div className="space-y-4 text-emerald-900">
                                <p>Our AI-powered platform transforms compliance from a burden into a strategic
                                    advantage, with intelligent automation handling the heavy lifting.</p>

                                {/* AI Metric Cards */}
                                <div
                                    className="grid grid-cols-2 gap-3 my-6 transition-opacity duration-300"
                                    style={{opacity: aiMetricOpacity === 0 ? 0 : 1}}>
                                    <SavingsMetricCard
                                        id="ai-time-saved"
                                        title="Time Saved"
                                        value="90%"
                                        secondaryValue="~32 hrs/week"
                                        icon={Clock}
                                        iconColor="text-emerald-600"
                                        bgColor="bg-emerald-100"
                                        textColor="text-emerald-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Automated document processing",
                                            "Instant regulatory mapping",
                                            "Auto-generated reports"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="ai-error-reduction"
                                        title="Error Rate"
                                        value="- 85%"
                                        secondaryValue="vs. manual process"
                                        icon={AlertCircle}
                                        iconColor="text-blue-600"
                                        bgColor="bg-blue-100"
                                        textColor="text-blue-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "AI verification checks",
                                            "Consistent interpretation",
                                            "Real-time validation"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="ai-cost-savings"
                                        title="Cost Savings"
                                        value="$120K+"
                                        secondaryValue="annually"
                                        icon={LineChart}
                                        iconColor="text-purple-600"
                                        bgColor="bg-purple-100"
                                        textColor="text-purple-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Reduced staffing needs",
                                            "Lower compliance costs",
                                            "Avoided penalties"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="ai-efficiency"
                                        title="Efficiency"
                                        value="4.2x"
                                        secondaryValue="faster implementation"
                                        icon={Percent}
                                        iconColor="text-amber-600"
                                        bgColor="bg-amber-100"
                                        textColor="text-amber-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Parallel processing",
                                            "Smart prioritization",
                                            "Continuous monitoring"
                                        ]}
                                    />
                                </div>

                                {/*<h4 className="text-xl font-semibold text-emerald-800 mt-6 mb-2">Streamlined*/}
                                {/*    Workflow:</h4>*/}
                                {/*<ol className="list-decimal pl-6 space-y-2">*/}
                                {/*    <li>AI continuously monitors regulatory updates</li>*/}
                                {/*    <li>Smart analysis identifies relevant changes for your business</li>*/}
                                {/*    <li>Custom AI agents generate implementation plans</li>*/}
                                {/*    <li>Automated documentation and task assignment</li>*/}
                                {/*    <li>Real-time compliance monitoring dashboard</li>*/}
                                {/*    <li>One-click comprehensive reporting</li>*/}
                                {/*    <li>Predictive compliance risk assessment</li>*/}
                                {/*</ol>*/}

                                {/*<h4 className="text-xl font-semibold text-emerald-800 mt-6 mb-2">Key Benefits:</h4>*/}
                                {/*<ul className="list-disc pl-6 space-y-2">*/}
                                {/*    <li>90% reduction in manual compliance work</li>*/}
                                {/*    <li>Consistent interpretation of regulations</li>*/}
                                {/*    <li>Seamless multi-framework compliance</li>*/}
                                {/*    <li>Reduced risk of non-compliance</li>*/}
                                {/*    <li>Compliance resources reallocated to strategy</li>*/}
                                {/*</ul>*/}

                                {/*<p className="mt-6">Most organizations can reduce compliance staffing needs*/}
                                {/*    by <strong>75%</strong> while simultaneously improving compliance coverage and*/}
                                {/*    reducing risk exposure.</p>*/}


                            </div>
                        </div>
                    </div>

                    {/* Before Content Mask */}
                    <div
                        className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 p-8 md:p-12 overflow-y-auto z-10"
                        style={{clipPath: `inset(0 0 0 ${position}%)`}}>
                        <div className="h-full flex flex-col justify-center max-w-xl mx-auto ml-auto">


                            <h1
                                ref={titleRef}
                                className="text-4xl md:text-6xl font-medium tracking-tight text-gray-900 dark:text-gray-100 mb-6 leading-tight text-center"


                            >

                                Mutliple Tools and Software



                                <br/>
                                <span
                                    className="bg-gradient-to-r from-emerald-700 to-green-600 bg-clip-text text-transparent">




                </span>
                            </h1>


                            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                                Traditional Compliance Process
                            </h3>
                            <div className="space-y-4 text-gray-700">
                                <p>The traditional compliance process requires intense manual effort, with teams
                                    spending countless hours reviewing documents and tracking change.</p>

                                {/* Traditional Metric Cards */}
                                <div
                                    className="grid grid-cols-2 gap-3 my-6 transition-opacity duration-300"
                                    style={{opacity: tradMetricOpacity === 0 ? 0 : 1}}>
                                    <SavingsMetricCard
                                        id="trad-time-required"
                                        title="Hours/Week"
                                        value="35+"
                                        secondaryValue="manual work"
                                        icon={Clock}
                                        iconColor="text-gray-600"
                                        bgColor="bg-gray-100"
                                        textColor="text-gray-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Document reviews",
                                            "Manual report creation",
                                            "Compliance meetings"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="trad-error-rate"
                                        title="Error Rate"
                                        value="18%"
                                        secondaryValue="avg. in documentation"
                                        icon={AlertCircle}
                                        iconColor="text-red-600"
                                        bgColor="bg-red-100"
                                        textColor="text-red-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Human oversight",
                                            "Inconsistent processes",
                                            "Manual data entry"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="trad-annual-cost"
                                        title="Annual Cost"
                                        value="$280K+"
                                        secondaryValue="compliance staff"
                                        icon={LineChart}
                                        iconColor="text-indigo-600"
                                        bgColor="bg-indigo-100"
                                        textColor="text-indigo-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "3-4 FTEs required",
                                            "External consultant fees",
                                            "Training & certification"
                                        ]}
                                    />
                                    <SavingsMetricCard
                                        id="trad-staff-needs"
                                        title="Staff Needs"
                                        value="3-4"
                                        secondaryValue="full-time employees"
                                        icon={Users}
                                        iconColor="text-gray-600"
                                        bgColor="bg-gray-100"
                                        textColor="text-gray-700"
                                        expandable={false}
                                        detailedBreakdown={[
                                            "Compliance officers",
                                            "Legal specialists",
                                            "Document processors"
                                        ]}
                                    />
                                </div>

                                {/*<h4 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Process Overview:</h4>*/}
                                {/*<ol className="list-decimal pl-6 space-y-2">*/}
                                {/*    <li>Manually review updated regulatory frameworks</li>*/}
                                {/*    <li>Identify changes affecting your organization</li>*/}
                                {/*    <li>Document the implications for each department</li>*/}
                                {/*    <li>Create compliance checklists and procedures</li>*/}
                                {/*    <li>Manually track implementation progress</li>*/}
                                {/*    <li>Prepare lengthy reports for executive review</li>*/}
                                {/*    <li>Schedule periodic compliance audits</li>*/}
                                {/*</ol>*/}

                                {/*<h4 className="text-xl font-semibold text-gray-800 mt-6 mb-2">Key Challenges:</h4>*/}
                                {/*<ul className="list-disc pl-6 space-y-2">*/}
                                {/*    <li>Time-consuming manual documentation</li>*/}
                                {/*    <li>Inconsistent interpretation of regulations</li>*/}
                                {/*    <li>Difficulty tracking multiple compliance frameworks</li>*/}
                                {/*    <li>Risk of human error in implementation</li>*/}
                                {/*    <li>Limited resources for thorough compliance</li>*/}
                                {/*</ul>*/}

                                {/*<p className="mt-6">This approach typically requires <strong>3-4 full-time*/}
                                {/*    employees</strong> with expertise in regulatory compliance, resulting in significant*/}
                                {/*    overhead costs and potential compliance gaps.</p>*/}


                            </div>
                        </div>
                    </div>

                    {/* Divider/handle */}
                    <div
                        className="absolute top-0 bottom-0 w-1 bg-white shadow-md cursor-ew-resize z-20"
                        style={{left: `${position}%`}}
                    >
                        <div
                            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white shadow-lg flex items-center justify-center">
                            <div className="flex flex-col items-center">
                                <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                          d="M7 16l-4-4m0 0l4-4m-4 4h18"/>
                                </svg>
                                <svg className="w-5 h-5 text-orange-500 -mt-1" fill="none" viewBox="0 0 24 24"
                                     stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                                          d="M17 8l4 4m0 0l-4 4m4-4H3"/>
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Labels */}
                    <div
                        className="absolute top-4 right-4 bg-white/80 px-3 py-1 rounded-md text-sm font-medium text-gray-700 shadow-sm z-20">
                        Traditional
                    </div>
                    <div
                        className="absolute top-4 left-4 bg-orange-500/90 px-3 py-1 rounded-md text-sm font-medium text-white shadow-sm z-20">
                        AI-Optimized
                    </div>
                </div>

                {/* Call-to-action buttons */}
                <motion.div
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    transition={{delay: 0.2, duration: 0.5}}
                    className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6 mt-12"
                >
                    <Button
                        size="lg"
                        className="bg-emerald-700 hover:bg-emerald-600 text-white px-7 py-6 rounded-lg transition-all duration-300 shadow-sm hover:shadow-md"
                        onClick={() => navigate('/tools')}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        <span className="font-medium mr-1">{t('homepage.tryFreeTools')}</span>
                        <motion.span
                            animate={hovered ? {x: 3} : {x: 0}}
                            transition={{type: "spring", stiffness: 300, damping: 20}}
                        >
                            <ArrowRight className="h-4 w-4"/>
                        </motion.span>
                    </Button>

                    <Button
                        size="lg"
                        variant="outline"
                        className="border border-gray-200 text-gray-800 hover:bg-gray-50 px-7 py-6 rounded-lg transition-all duration-300 font-medium dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50"
                        onClick={() => navigate('/platform')}
                    >
                        {t('homepage.learnMore')}
                    </Button>
                </motion.div>
            </div>
        </section>
    );
};

export default SwipeSlideSection;